import { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, ArrowRight, ExternalLink, Search } from 'lucide-react';
import { Link, Navigate, useParams } from 'react-router-dom';

type APIParameter = {
  name: string;
  in: 'header' | 'path' | 'query';
  required?: boolean;
  description?: string;
  schema?: Record<string, unknown>;
  example?: unknown;
};

type APIOperation = {
  operationId: string;
  summary: string;
  description: string;
  tags: string[];
  parameters?: APIParameter[];
  requestBody?: Record<string, any>;
  responses: Record<string, any>;
  'x-docs-section': string;
  'x-required-tenant-permission'?: string;
};

type OpenAPISpec = {
  info: { title: string; version: string; description: string };
  paths: Record<string, Record<string, APIOperation>>;
  'x-api-boundaries'?: { unavailable: string[]; billing_exception: string };
  'x-authentication'?: Record<string, unknown>;
  'x-status-standard'?: Record<string, string>;
};

type APIEntry = APIOperation & { method: string; path: string };

const apiBase = (import.meta.env.VITE_NEXUS_API_URL || 'https://cloud.elysiacloud.com/api/v1').replace(/\/$/, '');
const openAPIURL = `${apiBase}/nexus/openapi.json`;

const methodLabels: Record<string, string> = {
  get: 'read',
  post: 'create',
  put: 'replace',
  patch: 'update',
  delete: 'delete',
};

function useOpenAPI() {
  const [spec, setSpec] = useState<OpenAPISpec | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const controller = new AbortController();
    fetch(openAPIURL, { signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) throw new Error(`OpenAPI request returned ${response.status}`);
        setSpec(await response.json());
      })
      .catch((reason) => {
        if (reason?.name !== 'AbortError') setError(reason?.message || 'Unable to load the Nexus OpenAPI document.');
      });
    return () => controller.abort();
  }, []);

  return { spec, error };
}

function flattenOperations(spec: OpenAPISpec | null): APIEntry[] {
  if (!spec) return [];
  return Object.entries(spec.paths).flatMap(([path, methods]) =>
    Object.entries(methods).map(([method, operation]) => ({ ...operation, method, path })),
  );
}

function CodeBlock({ value }: { value: unknown }) {
  return <pre className="api-code"><code>{typeof value === 'string' ? value : JSON.stringify(value, null, 2)}</code></pre>;
}

function LoadingState({ error }: { error: string }) {
  if (error) {
    return (
      <div className="api-load-error">
        <strong>API reference unavailable</strong>
        <p>{error}</p>
        <a href={openAPIURL} target="_blank" rel="noreferrer">Open the OpenAPI document <ExternalLink size={14} /></a>
      </div>
    );
  }
  return <div className="api-loading">Loading the current Nexus API contract…</div>;
}

export function APIReferenceHome() {
  const { spec, error } = useOpenAPI();
  const operations = useMemo(() => flattenOperations(spec), [spec]);
  const sections = useMemo(() => Array.from(new Map(operations.map((operation) => [
    operation['x-docs-section'],
    operation.tags?.[0] || operation['x-docs-section'],
  ])).entries()), [operations]);

  if (!spec) return <LoadingState error={error} />;

  return (
    <div className="api-reference-page content-width">
      <header className="api-hero">
        <p className="eyebrow">Nexus user API</p>
        <h1>Build complete cloud products on Nexus.</h1>
        <p>{spec.info.description}</p>
        <div className="intro-actions">
          <Link to={`/api/${sections[0]?.[0]}`} className="primary-button">Browse endpoints <ArrowRight size={16} /></Link>
          <a href={openAPIURL} target="_blank" rel="noreferrer" className="secondary-button">OpenAPI 3.1 JSON <ExternalLink size={15} /></a>
        </div>
      </header>

      <section className="api-auth-grid">
        <div>
          <span>1</span>
          <h2>Create a tenant API key</h2>
          <p>Create the key in Nexus. The full secret is shown once. Expiration and source IP/CIDR restrictions are optional.</p>
        </div>
        <div>
          <span>2</span>
          <h2>Send tenant context</h2>
          <p>Every request includes the tenant ID and either a Bearer token or the X-API-Key header.</p>
        </div>
        <div>
          <span>3</span>
          <h2>Stay within IAM</h2>
          <p>Effective access is the intersection of key permissions and the owner’s current tenant role.</p>
        </div>
      </section>

      <section className="api-section">
        <div className="section-heading">
          <div><p className="eyebrow">Authentication</p><h2>Request format</h2></div>
        </div>
        <CodeBlock value={`curl "${apiBase}/nexus/me/services/cloud-compute" \\\n  -H "Authorization: Bearer ntk_your_key" \\\n  -H "X-Nexus-Tenant-ID: 2e24e4c3-dce8-4e85-9f04-417ad9dc1528"`} />
      </section>

      <section className="api-boundaries">
        <h2>Programmatic API boundaries</h2>
        <p>The user API intentionally does not expose account settings, tenant settings, tickets/support, or billing/payment operations. Read-only usage endpoints remain available.</p>
      </section>

      <section className="api-section">
        <div className="section-heading">
          <div><p className="eyebrow">Service reference</p><h2>{operations.length} documented operations</h2></div>
          <p>Each operation has its own page with request fields, examples, permissions, response objects, and status behavior.</p>
        </div>
        <div className="api-service-grid">
          {sections.map(([slug, label]) => {
            const count = operations.filter((operation) => operation['x-docs-section'] === slug).length;
            return <Link key={slug} to={`/api/${slug}`}><strong>{label}</strong><span>{count} operations</span><ArrowRight size={16} /></Link>;
          })}
        </div>
      </section>
    </div>
  );
}

export function APIServicePage() {
  const { section } = useParams();
  const { spec, error } = useOpenAPI();
  const [query, setQuery] = useState('');
  const operations = useMemo(() => flattenOperations(spec), [spec]);
  const sectionEntries = operations.filter((operation) => operation['x-docs-section'] === section);
  const entries = sectionEntries.filter((operation) =>
    `${operation.method} ${operation.path} ${operation.summary} ${operation.description}`.toLowerCase().includes(query.toLowerCase()),
  );
  const title = sectionEntries[0]?.tags?.[0];

  if (!spec) return <LoadingState error={error} />;
  if (!title) return <Navigate to="/api" replace />;

  return (
    <div className="api-reference-page content-width">
      <Link to="/api" className="back-link"><ArrowLeft size={15} /> API reference</Link>
      <header className="api-service-header">
        <p className="eyebrow">Service API</p>
        <h1>{title}</h1>
        <p>{entries.length} operations available to tenant API keys.</p>
      </header>
      <label className="api-filter"><Search size={17} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder={`Search ${title} endpoints`} /></label>
      <div className="api-operation-list">
        {entries.map((operation) => (
          <Link key={operation.operationId} to={`/api/${section}/${operation.operationId}`}>
            <span className={`api-method ${operation.method}`}>{operation.method}</span>
            <div><code>{operation.path}</code><p>{operation.description || operation.summary}</p></div>
            <ArrowRight size={17} />
          </Link>
        ))}
      </div>
    </div>
  );
}

function ParameterTable({ parameters }: { parameters: APIParameter[] }) {
  return (
    <div className="api-fields">
      {parameters.map((parameter) => (
        <div key={`${parameter.in}-${parameter.name}`}>
          <div><code>{parameter.name}</code><span>{parameter.in} · {String(parameter.schema?.type || 'string')}</span>{parameter.required && <em>required</em>}</div>
          <div><p>{parameter.description}</p>{parameter.example !== undefined && <small>Example: <code>{String(parameter.example)}</code></small>}</div>
        </div>
      ))}
    </div>
  );
}

function SchemaTable({ schema }: { schema?: Record<string, any> }) {
  const required = new Set<string>(schema?.required || []);
  const fields = Object.entries(schema?.properties || {});
  if (!fields.length) return <p className="api-empty">This operation has no JSON request object.</p>;
  return (
    <div className="api-fields">
      {fields.map(([name, field]: [string, any]) => (
        <div key={name}>
          <div><code>{name}</code><span>{field.type || 'object'}</span>{required.has(name) && <em>required</em>}</div>
          <div><p>{field.description}</p>{field.example !== undefined && <small>Example: <code>{String(field.example)}</code></small>}</div>
        </div>
      ))}
    </div>
  );
}

export function APIOperationPage() {
  const { section, operationId } = useParams();
  const { spec, error } = useOpenAPI();
  const operations = useMemo(() => flattenOperations(spec), [spec]);
  const operation = operations.find((entry) => entry.operationId === operationId && entry['x-docs-section'] === section);

  if (!spec) return <LoadingState error={error} />;
  if (!operation) return <Navigate to={`/api/${section || ''}`} replace />;

  const body = operation.requestBody?.content?.['application/json'];
  const curl = [
    `curl -X ${operation.method.toUpperCase()} "${apiBase}${operation.path.replace('/api/v1', '')}" \\`,
    '  -H "Authorization: Bearer ntk_your_key" \\',
    '  -H "X-Nexus-Tenant-ID: 2e24e4c3-dce8-4e85-9f04-417ad9dc1528"',
    body?.example ? `  -H "Content-Type: application/json" \\\n  --data '${JSON.stringify(body.example)}'` : '',
  ].filter(Boolean).join('\n');

  return (
    <div className="api-operation-page">
      <article>
        <Link to={`/api/${section}`} className="back-link"><ArrowLeft size={15} /> {operation.tags[0]}</Link>
        <header>
          <div className="api-route"><span className={`api-method ${operation.method}`}>{operation.method}</span><code>{operation.path}</code></div>
          <p className="eyebrow">{methodLabels[operation.method] || operation.method} operation</p>
          <h1>{operation.summary}</h1>
          <p>{operation.description}</p>
          <div className="api-permission"><strong>Required tenant permission</strong><code>{operation['x-required-tenant-permission'] || 'Authenticated tenant membership'}</code></div>
        </header>

        <section id="parameters"><h2>Parameters</h2><ParameterTable parameters={operation.parameters || []} /></section>
        <section><h2>Example request</h2><CodeBlock value={curl} /></section>
        {body && <section id="request-body"><h2>Request body object</h2><SchemaTable schema={body.schema} /><h3>Sample JSON</h3><CodeBlock value={body.example} /></section>}
        <section id="responses">
          <h2>Responses and status messages</h2>
          <div className="api-responses">
            {Object.entries(operation.responses).map(([status, response]: [string, any]) => (
              <div key={status}>
                <div><strong>{status}</strong><p>{response.description}</p></div>
                {response.content?.['application/json']?.example && <CodeBlock value={response.content['application/json'].example} />}
              </div>
            ))}
          </div>
        </section>
      </article>
      <aside>
        <p>Operation</p>
        <a href="#parameters">Parameters</a>
        {body && <a href="#request-body">Request body</a>}
        <a href="#responses">Responses</a>
        <a href={openAPIURL} target="_blank" rel="noreferrer">OpenAPI JSON <ExternalLink size={12} /></a>
      </aside>
    </div>
  );
}
